import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Text1 } from 'ui/shorthands/Texts'
import { addDays, addMinutes, endOfDay, isBefore } from 'date-fns'
import { ReservationDeadlineTimings, ReservationFrameProps } from 'types/med'
import {
  formatTimeString,
  parseDatetimeSecondString,
} from 'ui/utils/dateFormatUtils'
import { useStyles as textFieldStyles } from 'ui/shorthands/StackItemTextField'
import { jstNewDate } from 'ui/utils/jstDatetime'

type ReservationDateSelectProps = {
  departmentId?: number
  deadlineTiming?: ReservationDeadlineTimings | null
  frames?: Array<ReservationFrameProps>
  onSelect?: (startEnd?: ReservationFrameProps) => void
  hasError?: boolean
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: 196,
    overflowY: 'auto',
  },
  item: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  available: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  unavailable: {
    color: theme.palette.divider,
  },
  selected: {
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  selectedText: {
    color: theme.palette.primary.main,
  },
  unavailableDate: {
    display: 'flex',
    flex: 1,
    placeContent: 'center',
    placeItems: 'center',
    padding: theme.spacing(2),
  },
}))

const isEqualFrame = (a?: ReservationFrameProps, b?: ReservationFrameProps) => {
  if (!a || !b) return

  return (
    a.frame_datetime_start === b.frame_datetime_start &&
    a.frame_datetime_end === b.frame_datetime_end
  )
}

export const getDeadlineTimingDate: (
  timing?: ReservationDeadlineTimings | null,
  now?: Date // NOTE: テストのためのデフォルト引数
) => Date = (timing, now = jstNewDate) => {
  switch (timing) {
    case '30_minutes_before':
      return addMinutes(now, 30)
    case 'day_before':
      return endOfDay(now)
    case 'day_before_12:00':
      return now.getHours() < 12 ? endOfDay(now) : addDays(endOfDay(now), 1)
    default:
      return now
  }
}

const SelectFrames: React.FC<ReservationDateSelectProps> = ({
  departmentId,
  deadlineTiming,
  frames,
  onSelect,
  hasError,
}: ReservationDateSelectProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const textFieldClasses = textFieldStyles().classes

  const [selectedFrame, setSelectedFrame] = useState<
    ReservationFrameProps | undefined
  >()
  const selectHandler = (index: number, startEnd: ReservationFrameProps) => {
    if (startEnd) {
      onSelect?.(startEnd)
      setSelectedFrame(startEnd)
    }
  }

  const filteredFrames =
    frames?.filter((frame) => {
      const isNotPast = isBefore(
        getDeadlineTimingDate(deadlineTiming),
        parseDatetimeSecondString(frame.frame_datetime_start)
      )
      const hasAvailableCapacity =
        frame.reserved_people_count < frame.number_of_people
      return isNotPast && hasAvailableCapacity
    }) || []

  useEffect(() => {
    onSelect?.(undefined)
    setSelectedFrame(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId])

  return (
    <div className={classes.root}>
      {
        filteredFrames.length ? (
          filteredFrames.map((frame, i) => {
            const start = parseDatetimeSecondString(frame.frame_datetime_start)
            const end = parseDatetimeSecondString(frame.frame_datetime_end)
            return (
              <div
                key={i}
                className={cx(
                  classes.item,
                  classes.available,
                  (!selectedFrame || hasError) && textFieldClasses.required,
                  isEqualFrame(frame, selectedFrame) && classes.selected
                )}
                onClick={() => {
                  selectHandler(i, frame)
                }}
              >
                <Text1
                  className={cx(
                    isEqualFrame(frame, selectedFrame) && classes.selectedText
                  )}
                >
                  {formatTimeString(start)}~{formatTimeString(end)}
                </Text1>
              </div>
            )
          })
        ) : frames ? (
          <Text1 className={classes.unavailableDate}>
            {t('この日は予約ができません。別の日を選択してください。')}
          </Text1>
        ) : null // NOTE: framesがundefinedの時は表示なし
      }
    </div>
  )
}

export default SelectFrames

export const SelectFramesStyles = useStyles
